import { Signal } from 'typed-signals';
import type { TileColor } from '../../TileColor';
import type { TrayRefillSettings } from './TrayRefillRandomizer';
import { TrayRefillRandomizer } from './TrayRefillRandomizer';
import type { TrayState } from '../../types';
import { TileStackModel } from '../board/TileStackModel';
import { TileModel } from '../board/TileModel';
import type { RefillMiddleware } from './RefillMiddleware';
import type { BoardModel } from '../board/BoardModel';
import { TrayMatchChanceOverride } from './TrayMatchChanceOverride';

export interface TrayModelSettings {
  readonly boardModel: BoardModel;
  readonly maxStacks: number;
  readonly refills: TrayRefillSettings[];
  readonly refillColors: TileColor[];
}

export class TrayModel {
  public onRefilled = new Signal();

  private readonly stackQueue: TileColor[][] = [];

  public readonly refillRandomizer: TrayRefillRandomizer;

  private readonly items: TileStackModel[] = [];

  private readonly matchChanceOverride: TrayMatchChanceOverride;

  constructor(
    private readonly settings: TrayModelSettings,
    state?: TrayState,
  ) {
    if (state?.colors) this.stackQueue.push(...state.colors);
    const { refills, refillColors } = settings;
    this.refillRandomizer = new TrayRefillRandomizer(refills, refillColors);
    this.matchChanceOverride = new TrayMatchChanceOverride(settings);
    this.initItems();
  }

  private initItems(): void {
    const { maxStacks } = this.settings;
    for (let i = this.items.length; i < maxStacks; i += 1) {
      this.items.push(new TileStackModel(i, 0));
    }
  }

  public refillItems(allowEmptyTray = false): void {
    const { items, stackQueue, refillRandomizer } = this;
    const nextColors: TileColor[][] = [];

    items.forEach((item) => {
      item.removeAllTiles();
    });

    do {
      nextColors.length = 0;
      for (let stackToRefill = 0; stackToRefill < items.length; stackToRefill += 1) {
        const colors = stackQueue.shift() ?? refillRandomizer.getTileColors();
        nextColors.push(colors);
      }
    } while (!allowEmptyTray && nextColors.flat().length === 0);

    this.matchChanceOverride.performOverride(nextColors);

    items.forEach((item, index) => {
      const tileColors = nextColors[index];
      item.addTiles(tileColors.map((color) => new TileModel(color)));
    });

    this.onRefilled.emit();
  }

  public getItems(): TileStackModel[] {
    return this.items;
  }

  public getTopColors(): TileColor[] {
    return this.items.map((item) => item.getTopmostColor());
  }

  public hasActiveTiles(): boolean {
    return this.items.some((stack) => stack.hasTiles());
  }

  public exportState(): TrayState {
    const { items } = this;

    const colors: TileColor[][] = [];
    const colorsFromStacks = items.map((item) => item.getTiles().map(({ color }) => color));

    colors.push(...colorsFromStacks);
    colors.push(...this.stackQueue);

    return { colors };
  }

  public exportStackQueue(): TileColor[][] {
    return this.stackQueue.slice();
  }

  public setRefillMiddleware(middleware: RefillMiddleware | null): void {
    this.refillRandomizer.setMiddleware(middleware);
  }

  public setState(state: TrayState): void {
    this.stackQueue.length = 0;
    this.stackQueue.push(...state.colors);
    this.refillItems();
  }

  public updateProgress(combinedProgress: number): void {
    this.refillRandomizer.updateProgress(combinedProgress);
    this.matchChanceOverride.updateLevelProgress(combinedProgress);
  }
}
